import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MessageSquare, PackageX, Send, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Kpi, PageHead } from "@/components/Bits";
import { ExportButtons } from "@/components/ExportButtons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const EMPTY = { to: "admin", subject: "", body: "" };

export default function Alerts() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const isNetwork = !!user.is_network;

  const load = () => api.get("/alerts").then(({ data }) => setData(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    try {
      await api.post("/messages", form);
      toast.success("Note sent to HQ");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const resolve = async (m) => {
    try {
      await api.post(`/messages/${m.id}/resolve`);
      toast.success("Marked resolved");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!data) return null;
  const canResolve = data.can_resolve;

  return (
    <div>
      <PageHead
        title="Alerts"
        subtitle={isNetwork ? "Actionable signals across the network" : user.studio_name}
      >
        <div className="flex flex-wrap gap-2">
          <Button
            className="min-h-[44px]"
            data-testid="message-hq-btn"
            onClick={() => {
              setForm(EMPTY);
              setOpen(true);
            }}
          >
            <Send className="mr-2 h-4 w-4" /> Message HQ
          </Button>
          <ExportButtons
            testidPrefix="alerts"
            filename="veloskin-alerts"
            title="Alerts"
            subtitle={isNetwork ? "All studios" : user.studio_name}
            columns={[
              { label: "Studio", value: (r) => r.studio_name },
              { label: "Material", value: (r) => r.name },
              { label: "On hand", value: (r) => `${r.quantity.toFixed(2)} ${r.unit}` },
              { label: "Reorder at", value: (r) => r.low_threshold },
              { label: "Shortfall", value: (r) => r.shortfall },
            ]}
            rows={data.low_stock}
          />
        </div>
      </PageHead>

      {(data.service_missing || []).length > 0 && (
        <Card
          className="mb-6 border-2 border-amber-400 bg-amber-50 shadow-none"
          data-testid="alerts-service-missing"
        >
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg text-amber-900">
              <TriangleAlert className="h-5 w-5 text-amber-600" />
              {data.service_missing.length} delivered job
              {data.service_missing.length > 1 ? "s" : ""} missing service &amp; products
            </CardTitle>
            <p className="mt-1 text-xs text-amber-800">
              These jobs reached the Delivered stage without any service or product logged. The
              warranty cannot be registered until you open each job and add the service and products
              used. Do this now so the customer's warranty can be issued.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.service_missing.map((m) => (
              <div
                key={m.job_id}
                data-testid={`alert-missing-${m.job_id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-white p-3"
              >
                <div>
                  <p className="text-sm font-medium text-brand-ink">
                    {m.customer_name || "Customer"}
                    {m.vehicle && <span className="text-slate-500"> · {m.vehicle}</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {isNetwork && `${m.studio_name} · `}
                    {m.status}
                    {m.delivered_on && ` · delivered ${fmtDate(m.delivered_on)}`}
                  </p>
                </div>
                <Button
                  asChild
                  size="sm"
                  className="min-h-[40px] bg-amber-600 hover:bg-amber-700"
                  data-testid={`fix-missing-job-${m.job_id}`}
                >
                  <Link to={`/jobs/${m.job_id}`}>Add service &amp; products</Link>
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="mb-6 grid gap-6 sm:grid-cols-3">
        <Kpi
          label="Missing service"
          value={data.counts.service_missing || 0}
          testid="kpi-service-missing"
          accent="text-amber-600"
        />
        <Kpi
          label="Low stock"
          value={data.counts.low_stock}
          testid="kpi-low-stock"
          accent="text-amber-600"
        />
        <Kpi label="Open notes" value={data.counts.messages} testid="kpi-open-messages" />
      </div>

      <Card className="mb-6 border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Low stock</CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Materials at or below the reorder level set for that studio.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table data-testid="low-stock-table" className="mobile-cards">
            <TableHeader>
              <TableRow>
                {isNetwork && <TableHead>Studio</TableHead>}
                <TableHead>Material</TableHead>
                <TableHead className="text-right">On hand</TableHead>
                <TableHead className="text-right">Reorder at</TableHead>
                <TableHead className="text-right">Shortfall</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.low_stock.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-slate-500">
                    Every tracked material is above its reorder level.
                  </TableCell>
                </TableRow>
              )}
              {data.low_stock.map((r) => (
                <TableRow
                  key={r.stock_id}
                  className="hover:bg-brand-tint/60"
                  data-testid={`low-stock-row-${r.stock_id}`}
                >
                  {isNetwork && (
                    <TableCell data-label="Studio" className="text-slate-500">
                      {r.studio_name}
                    </TableCell>
                  )}
                  <TableCell data-label="Material" className="font-medium">
                    {r.name}
                    <span className="block text-xs text-slate-500">{r.category}</span>
                  </TableCell>
                  <TableCell data-label="On hand" className="text-right">
                    <Badge className="border-0 bg-amber-50 text-amber-700">
                      <PackageX className="mr-1 h-3 w-3" /> {r.quantity.toFixed(2)} {r.unit}
                    </Badge>
                  </TableCell>
                  <TableCell data-label="Reorder at" className="text-right text-slate-500">
                    {r.low_threshold} {r.unit}
                  </TableCell>
                  <TableCell data-label="Shortfall" className="text-right font-semibold text-crimson-600">
                    {r.shortfall.toFixed(2)} {r.unit}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>


      <Card className="border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">
            {canResolve ? "Notes sent to HQ and the central team" : "Notes you sent to HQ"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="messages-list">
          {data.messages.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">
              No notes yet — use Message HQ to raise something.
            </p>
          )}
          {data.messages.map((m) => (
            <div
              key={m.id}
              data-testid={`message-${m.id}`}
              className={`rounded-md border p-4 ${
                m.resolved ? "border-brand-line bg-slate-50" : "border-crimson-200 bg-crimson-50/40"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-brand-ink">
                    {m.subject || "Note to HQ"}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {m.from_name} · {m.from_role_label} · {m.studio_name} · to{" "}
                    {m.to === "admin" ? "Admin" : "Central team"} · {fmtDate(m.created_at)}
                  </p>
                </div>
                {m.resolved ? (
                  <Badge className="border-0 bg-emerald-50 text-emerald-700">
                    <CheckCircle2 className="mr-1 h-3 w-3" /> Resolved
                    {m.resolved_by ? ` by ${m.resolved_by}` : ""}
                  </Badge>
                ) : (
                  canResolve && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="min-h-[44px]"
                      data-testid={`resolve-message-${m.id}`}
                      onClick={() => resolve(m)}
                    >
                      Mark resolved
                    </Button>
                  )
                )}
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{m.body}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="message-dialog">
          <DialogHeader>
            <DialogTitle>Send a note to HQ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Send to</Label>
              <Select value={form.to} onValueChange={(v) => setForm((f) => ({ ...f, to: v }))}>
                <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="message-to-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="central">Central team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                className="mt-1.5 min-h-[44px]"
                data-testid="message-subject-input"
                maxLength={120}
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              />
            </div>
            <div>
              <Label>Note *</Label>
              <Textarea
                className="mt-1.5"
                rows={5}
                maxLength={2000}
                data-testid="message-body-input"
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              className="min-h-[44px]"
              data-testid="send-message-btn"
              onClick={send}
              disabled={!form.body.trim()}
            >
              <MessageSquare className="mr-2 h-4 w-4" /> Send note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
